
import { Component, Prop, Watch } from "vue-property-decorator";
import { mixins } from "vue-class-component";

import GET_ACTIVITIES from "@/apollo/projectActivities/GetActivities.gql";

import GetRelativeRouteNameMixin from "@/mixins/route/GetRelativeRouteNameMixin.vue";
import CardPagination from "@/components/card/PaginationNew.vue";
import moment from "moment";
import {
  User,
  Company,
  Project,
  Project_Activity,
  Query_RootProjectArgs,
} from "@/generated/graphql";
import DefaultContentLayout from "@/components/scaffold/DefaultContentLayoutNew.vue";
import ClientFilters from "@/components/companyProjects/ClientMasterviewFilters.vue";
import LoadingOverlay from "@/components/common/LoadingOverlay.vue";
import EmptyListPlaceholder from "@/components/common/EmptyListPlaceholder.vue";
import CardBodyItem from "@/components/card/CardBodyItem.vue";
import ActivityFormModal from "@/components/projects/activities/ActivityFormModalNew.vue";
import projectsFiltersTemplate from "@/data/filtersTemplates/projectsFiltersTemplate";
import RouteNames from "@/entities/RouteNames";
import Roles from "@/entities/Roles";
import PaginationConfig from "@/entities/PaginationConfig";
import paginationConfig from "@/data/paginationConfig";
import ProjectsFiltersQuery from "@/entities/filters/ProjectsFiltersQuery";
import {
  getFiltersDataFromQuery,
  joinQueryValues,
} from "@/helpers/router/prepareFiltersQuery";
import { cloneDeep, isEqual } from "lodash-es";
import {
  getPaginationDataFromQuery,
  getPaginationQuery,
} from "@/helpers/router/preparePaginationQuery";
import EmptyListImageProjects from "@/components/icons/emptyListImages/EmptyListImageProjects.vue";
import VisitsCardWrapper from "@/components/projects/VisitsCardWrapper.vue";

interface ProjectFilter extends Query_RootProjectArgs {}

@Component({
  components: {
    VisitsCardWrapper,
    EmptyListImageProjects,
    DefaultContentLayout,
    ActivityFormModal,
    ClientFilters,
    EmptyListPlaceholder,
    CardPagination,
    LoadingOverlay,
    CardBodyItem,
  },
  apollo: {
    activities: {
      query: GET_ACTIVITIES,
      variables() {
        return this.projectFilter;
      },
      update(data) {
  this.paginationConfig.total = data.total.aggregate.count;
  this.paginationConfig.loadedCount = data.project_activity.length;

  const sortedActivities = [...data.project_activity].sort((a, b) => {
    const dateA = a.start_date ? new Date(a.start_date).getTime() : 0;
    const dateB = b.start_date ? new Date(b.start_date).getTime() : 0;

    return dateB - dateA; // Descending: latest date first
  });

  return sortedActivities;
}

    },
  },
})
export default class PendingVisits extends mixins(GetRelativeRouteNameMixin) {
  @Prop({ type: Object, required: true }) readonly company!: Company;
  @Prop({ type: Object, required: true }) readonly currentUser!: User;
  @Prop({ type: Number, required: true }) readonly assignedTotal!: number;

  expandedDescriptions: { [key: string]: boolean } = {};
  descriptionMaxLength = 100;
  dialog: boolean = false;

  selectedProject?: Project = undefined;

  activities: Project_Activity[] | null = null;

  paginationConfig: PaginationConfig = { ...paginationConfig };

  projectFilters: ProjectsFiltersQuery = cloneDeep(projectsFiltersTemplate);
  RouteNames: typeof RouteNames = RouteNames;

  get hasData(): boolean {
    return !this.isLoading && Boolean(this.activities);
  }

  get filtersEmpty(): boolean {
    return isEqual(this.projectFilters, projectsFiltersTemplate);
  }

  get emptyListPlaceholder(): string {
    if (this.filtersEmpty) {
      return "You currently do not have any assigned visits";
    }

    return `You currently do not have any assigned visits`;
  }

  get dashboardRoute() {
    return {
      name: RouteNames.DASHBOARD,
    };
  }

  get isLoading(): boolean {
    return this.$apollo.queries.activities.loading;
  }

  get isManager(): boolean {
    return this.currentUser.role === "MANAGER";
  }

  get empty(): boolean {
    if (!this.activities) {
      return false;
    }

    return this.activities.length === 0;
  }

  get currentUserManagerId(): string {
    return this.currentUser.manager!.id;
  }

  get projectFilter(): ProjectFilter {
    const { page, limit } = this.paginationConfig;
    const nextOffset = page * limit - limit;

    const { createdAt, title, managers, contractors, location } =
      this.projectFilters;

    const filterConditions = [];

    filterConditions.push({
      accepted: {
        _eq: true,
      },
      deleted: { _eq: false },
      completed: { _eq: false },
      project: {
        company_id: { _eq: this.company.id },
      },
    });

    if (managers.length) {
      filterConditions.push({
        manager: {
          id: { _in: managers },
        },
      });
    }

    if (this.isManager) {
      filterConditions.push({
        project: {
          manager: {
            id: { _eq: this.currentUserManagerId },
          },
        },
      });
    }

    if (contractors.length) {
      filterConditions.push({
        contractor: {
          id: {
            _in: contractors,
          },
        },
      });
    }

    if (location) {
      filterConditions.push({
        location: { _ilike: `%${location}%` },
      });
    }

    if (title) {
      filterConditions.push({
        project: {
          client_name: { _ilike: `%${title}%` },
        },
      });
    }

   if (createdAt) {
      const startDate = moment(createdAt).utc().startOf("day").toISOString();
      const endDate = moment(createdAt).utc().endOf("day").toISOString();

      const datesConditions = [
        { start_date: { _gte: startDate } },
        { start_date: { _lte: endDate } },
      ];

      filterConditions.push({
        _and: datesConditions,
      });
    }

    return {
      offset: nextOffset,
      limit: limit,
      where: {
        _and: filterConditions,
      },
    };
  }

  get isAdmin(): boolean {
    return this.currentUser.role === Roles.ADMIN;
  }

  get queryPropsToWatch(): string {
    return JSON.stringify([this.projectFilters, this.paginationConfig]);
  }

  get queryParams(): {
    filters: ProjectsFiltersQuery;
    pagination: PaginationConfig;
  } {
    const query = this.$route.query;

    const { created_at: createdAt, ...queryParams } = query;

    const queryData = {
      createdAt,
      ...queryParams,
    };

    const filters = getFiltersDataFromQuery<ProjectsFiltersQuery>(
      projectsFiltersTemplate,
      queryData
    );
    const pagination = {
      ...this.paginationConfig,
      ...getPaginationDataFromQuery(query),
    };

    return {
      filters,
      pagination,
    };
  }

  get isChosenPageEmpty(): boolean {
    const collectionEmpty = this.activities?.length === 0;
    const notFirstPage = this.paginationConfig.page !== 1;

    return !this.isLoading && collectionEmpty && notFirstPage;
  }

  @Watch("queryPropsToWatch")
  updateQuery(): void {
    if (this.isChosenPageEmpty) {
      return this.showFirstPage();
    }

    const paginationQuery = getPaginationQuery(this.paginationConfig);

    const { createdAt, location, ...queryParams } = this.projectFilters;

    const query = joinQueryValues({
      ...paginationQuery,
      ...queryParams,
      created_at: createdAt,
    });

    if (isEqual(query, this.$route.query)) {
      return;
    }

    this.$router.push({ query }).catch(() => {});
  }

  @Watch("$route.query", { immediate: true })
  onQueryChange(): void {
    const { filters, pagination } = this.queryParams;

    this.projectFilters = filters;
    this.paginationConfig = pagination;
  }

  updateFilters(filters: ProjectsFiltersQuery): void {
    const dataEqual = isEqual(filters, this.projectFilters);

    if (dataEqual) {
      return;
    }

    this.projectFilters = filters;
  }

  resetFilters(): void {
    if (isEqual(this.projectFilters, projectsFiltersTemplate)) {
      return;
    }

    this.projectFilters = cloneDeep(projectsFiltersTemplate);
    this.paginationConfig = { ...paginationConfig };
  }

  showFirstPage(): void {
    this.paginationConfig = {
      ...this.paginationConfig,
      page: paginationConfig.page,
    };
  }

  refetchVisits(): void {
    this.$apollo.queries.activities.refetch();
    this.$emit("visitUpdated");
  }
}
